import React from "react";
import { Link } from "react-router-dom";
import { twMerge } from "tailwind-merge";

import { Label, Small } from "@fronterahealth/frontera-ui-components";

import { EhrProgrammingGoal } from "@pages/ClientEhrProgramming/EhrProgrammingItemDetail";
import {
  ehrGoalPath,
  ehrTargetPath,
  ehrUnmatchedSessionDataPath,
} from "@pages/ClientEhrProgramming/ehrProgrammingPaths";

interface EhrProgrammingSidebarProps {
  clientId: string;
  goals: EhrProgrammingGoal[];
  selectedGoalId: string | undefined;
  selectedTargetId: string | undefined;
  unmatchedDataPointCount: number;
  isUnmatchedSelected: boolean;
}

interface SidebarRowProps {
  to: string;
  name: string;
  isSelected: boolean;
  indented?: boolean;
}

const SidebarRow: React.FC<SidebarRowProps> = ({ to, name, isSelected, indented }) => (
  <li>
    <Link
      to={to}
      aria-current={isSelected ? "page" : undefined}
      className={twMerge(
        "block rounded-sm px-2 py-1.5",
        indented && "pl-6",
        isSelected ? "bg-limestone-200 hover:bg-limestone-200" : "hover:bg-limestone-50",
      )}
    >
      <Label
        size="small"
        displayType={isSelected ? "loud" : "normal"}
        colorType="primary"
        className="block truncate ph-no-capture"
        title={name}
      >
        {name}
      </Label>
    </Link>
  </li>
);

// Goals with their targets indented underneath, like the reassessment goals sidebar.
export const EhrProgrammingSidebar: React.FC<EhrProgrammingSidebarProps> = ({
  clientId,
  goals,
  selectedGoalId,
  selectedTargetId,
  unmatchedDataPointCount,
  isUnmatchedSelected,
}) => (
  <nav
    aria-label="Goals and targets"
    className="sticky top-0 max-h-[calc(100vh-16rem)] w-72 shrink-0 overflow-y-auto rounded-lg border border-limestone-200 bg-white p-2"
  >
    {goals.length ? (
      <ul className="flex flex-col gap-0.5">
        {goals.map((goal) => (
          <React.Fragment key={goal.id}>
            <SidebarRow
              to={ehrGoalPath(clientId, goal.id)}
              name={goal.name || "Untitled goal"}
              isSelected={goal.id === selectedGoalId && !selectedTargetId}
            />
            {goal.targets.map((target) => (
              <SidebarRow
                key={target.id}
                to={ehrTargetPath(clientId, goal.id, target.id)}
                name={target.name || "Untitled target"}
                isSelected={target.id === selectedTargetId}
                indented
              />
            ))}
          </React.Fragment>
        ))}
      </ul>
    ) : (
      <Small className="block p-2 text-text-secondary">No EHR programming stored for this client.</Small>
    )}
    {unmatchedDataPointCount > 0 && (
      <ul className="mt-2 border-t border-limestone-100 pt-2">
        <SidebarRow
          to={ehrUnmatchedSessionDataPath(clientId)}
          name={`Unmatched session data (${unmatchedDataPointCount})`}
          isSelected={isUnmatchedSelected}
        />
      </ul>
    )}
  </nav>
);
